import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../redux/authSlice';
import ProfileTabs from './ProfileTabs';
import ProfileForm from './ProfileForm';
import ProgressIndicator from './ProgressIndicator';
import AvatarUploader from './AvatarUploader';
import "./ProfilePage.css";

const ProfilePage = () => {
    const dispatch = useDispatch();
    const { roles } = useSelector((state) => state.auth);
    const [localUser, setLocalUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [activeTab, setActiveTab] = useState('personal');
    const [uploadProgress] = useState(0);

    const isFormComplete = () => {
        const allRequired = Object.values(requiredFields).flat();
        return allRequired.every(field => formData[field]);
    };

    const handleSubmit = async () => {
        try {
            const response = await fetch("/api/users/profile", {
                method: "PUT",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) throw new Error("Ошибка обновления профиля");

            const updatedUser = await response.json();
            setLocalUser(updatedUser);
            dispatch(setUser({
                user: updatedUser,
                roles: updatedUser.roles.map(role => role.name),
            }));

            setIsEditing(false);
            setMessage("Профиль успешно обновлен!");

        } catch (error) {
            console.error("Ошибка:", error);
            setMessage("Ошибка при обновлении профиля");
        }
    };


    const handleJoinOrganization = async () => {
        try {
            const response = await fetch("/api/users/join-organization", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                const errorMessage = await response.text();
                setMessage(errorMessage);
                return;
            }

            const updatedUserResponse = await fetch(`/api/users/profile`, {
                method: "GET",
                credentials: "include",
            });
            const updatedUser = await updatedUserResponse.json();

            setLocalUser(updatedUser);
            dispatch(setUser({
                user: updatedUser,
                roles: updatedUser.roles.map(role => role.name),
            }));

            setMessage("Заявка на вступление в организацию подана.");
        } catch (error) {
            console.error("Ошибка при отправке заявки:", error);
            setMessage("Произошла ошибка при отправке заявки.");
        }
    };

    const handleRevokeJoining = async () => {
        try {
            const response = await fetch("/api/users/revoke-joining", {
                method: "DELETE",
                credentials: "include",
            });

            if (!response.ok) {
                const errorMessage = await response.text();
                setMessage(errorMessage);
                return;
            }

            // Обновляем данные пользователя
            const updatedUserResponse = await fetch(`/api/users/profile`, {
                method: "GET",
                credentials: "include",
            });
            const updatedUser = await updatedUserResponse.json();

            setLocalUser(updatedUser);
            dispatch(setUser({
                user: updatedUser,
                roles: updatedUser.roles.map(role => role.name),
            }));

            setMessage("Заявка успешно отозвана!");
        } catch (error) {
            console.error("Ошибка при отзыве заявки:", error);
            setMessage("Произошла ошибка при отзыве заявки");
        }
    };

    const requiredFields = {
        personal: ['firstName', 'lastName', 'birthDate', 'gender', 'nationality'],
        contacts: ['email', 'phoneNumber', 'address'],
        documents: ['passportSeries', 'passportNumber', 'passportIssuedBy', 'passportIssueDate'],
        membership: ['motivation']
    };

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await fetch('/api/users/profile', {
                    method: 'GET',
                    credentials: 'include'
                });
                const data = await response.json();
                setLocalUser(data);
                setFormData(data);
                dispatch(setUser({
                    user: data,
                    roles: data.roles.map(role => role.name),
                }));
            } catch (error) {
                console.error('Error fetching user data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, []);

    const handleAvatarUpload = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch('/api/users/avatar', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if (!response.ok) throw new Error('Ошибка загрузки аватарки');
            const updatedUser = await response.json();
            setLocalUser(updatedUser);
            dispatch(setUser({
                user: updatedUser,
                roles: updatedUser.roles.map(role => role.name),
            }));
        } catch (error) {
            console.error('Upload failed:', error);
        }
    };

    const calculateCompletion = (tab) => {
        const fields = requiredFields[tab];
        const filled = fields.filter(field => formData[field]).length;
        return Math.round((filled / fields.length) * 100);
    };

    if (loading) return <div className="loading-spinner" />;

    return (
        <div className="profile-container">
            <div className="profile-header">
                <AvatarUploader
                    avatarUrl={localUser?.avatarUrl}
                    onUpload={handleAvatarUpload}
                    uploadProgress={uploadProgress}
                />
            </div>

            {isEditing ? (
                <ProfileForm
                    formData={formData}
                    setFormData={setFormData}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onCancel={() => setIsEditing(false)}
                    onSubmit={handleSubmit}
                />
            ) : (
                <ProfileTabs
                    user={localUser}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    onEdit={() => setIsEditing(true)}
                    roles={roles}
                    isFormComplete={isFormComplete()}
                    onJoinOrganization={handleJoinOrganization}
                    onRevokeJoining={handleRevokeJoining}
                    progress={{
                        personal: calculateCompletion('personal'),
                        contacts: calculateCompletion('contacts'),
                        documents: calculateCompletion('documents'),
                        membership: calculateCompletion('membership')
                    }}
                />
            )}
        </div>
    );
};

export default ProfilePage;